import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { MessageType, MessageStatus, SenderType } from '@constants/inbox';
import { useAccountPeople, useTranslation } from '@hooks';
import { INTERNAL_NOTE_COLORS } from '../internalNoteColors';
import MessageContent from './MessageContent';

const MessageWrapper = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'type' &&
        prop !== 'multiple' &&
        prop !== 'isLast' &&
        prop !== 'contentType' &&
        prop !== 'withReply' &&
        prop !== 'senderType' &&
        prop !== 'messageStatus',
})(
    ({ type, multiple, isLast, senderType, messageStatus }) => `
    padding: 12px;
    width: fit-content;
    background-color: ${
        type === MessageType.Note
            ? INTERNAL_NOTE_COLORS.background
            : type === MessageType.Outgoing
              ? messageStatus === MessageStatus.Failed
                  ? 'rgba(176, 0, 32, 0.25)'
                  : 'var(--secondary-light)'
              : '#F2F2F2'
    };
    border-radius: ${type === MessageType.Incoming ? '8px 8px 8px 0px' : '8px 8px 0px 8px'};
    background-color: ${type === MessageType.Outgoing && senderType === SenderType.Bot && '#E4EAEB'};
    & svg > path {
        fill: rgba(0, 0, 0, 0.87);
    }
    ${
        multiple &&
        `
        border-radius: 8px;
        &:not(:last-child) {
            margin-bottom: 4px;
        }
    `
    }
    ${
        multiple &&
        isLast &&
        `
        border-radius: ${type === MessageType.Incoming ? '8px 8px 8px 0' : '8px 8px 0 8px'};
    `
    }
`,
);

const ReplyWrapper = styled('div')`
    padding-left: 5px;
    border-left: 2px solid rgba(0, 0, 0, 0.12);
    margin-bottom: 6px;
`;

const MessageContentWrapper = ({
    data,
    type,
    isLast,
    multiple,
    locations,
    assignedLocation,
    isOutgoing,
    isEmail,
    messageStatus,
}) => {
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const senderName = data.replyMessage ? getPersonName(data.replyMessage.senderId) : '';

    const internalName = useMemo(() => {
        if (!data.replyMessage) return '';
        return locations.find(l => l.companyId === assignedLocation)?.internalName;
    }, [data.replyMessage?.companyId, locations]);

    const withReply = !!data.replyMessage;

    return (
        <MessageWrapper
            type={type}
            multiple={multiple}
            isLast={isLast}
            senderType={data.senderType}
            messageStatus={messageStatus}
        >
            {type === MessageType.Note ? (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontWeight: 500,
                        color: INTERNAL_NOTE_COLORS.label,
                        marginBottom: '4px',
                    }}
                >
                    <VisibilityOffOutlinedIcon
                        sx={{ fontSize: 12, '& > path': { fill: `${INTERNAL_NOTE_COLORS.label} !important` } }}
                    />
                    {t('InboxPage.internalNote')}
                </Typography>
            ) : null}
            {withReply ? (
                <ReplyWrapper>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.60)', marginBottom: '5px', display: 'block' }}
                    >
                        {isOutgoing ? senderName : internalName}
                    </Typography>
                    <MessageContent data={data.replyMessage} isOutgoing={isOutgoing} isEmail={isEmail} />
                </ReplyWrapper>
            ) : null}
            <MessageContent data={data} isOutgoing={isOutgoing} isEmail={isEmail} />
        </MessageWrapper>
    );
};

export default MessageContentWrapper;
